import {
  AppstoreOutlined,
  CheckCircleOutlined,
  EditOutlined,
  FileOutlined,
  FileTextOutlined,
  IconCartGlyph,
  IconListView,
  IconPaperPlane,
  SettingOutlined,
  TableOutlined,
} from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { cx } from '../../../components/ui/cx'
import { STAGING_CTA_LABEL } from '../staging-cta'
import type { StagingCta } from '../staging-cta'
import type { StagingSummary } from '../staging-types'

export interface StagingSummaryCardProps {
  /** UID-402 — now OPTIONAL. The card used to be rendered only when
   * `triage.summary` was present, which also gated the session's next-step
   * CTAs on an unrelated optional roll-up field. The CTA group is the page's
   * primary action and must not depend on the file-makeup tiles existing, so
   * the tiles are what's conditional now. */
  readonly summary?: StagingSummary | undefined
  /**
   * 2026-09-08 defect fix (REVISED) — this is the design's `_isBomSess`
   * session-type switch (`Fictiv Platform.dc.html:10552-10554`), NOT an
   * independent per-tile flag. Reading that view-model literally:
   *   `hasBomSource: _isBomSess`
   *   `showModels: (!_isBomSess || nModels > 0)`
   *   `showDrawings: (!_isBomSess || nDrawings > 0)`
   *   `hasAdditional: (!_isBomSess && nAdditional > 0)`
   *   `nAdditional: (_isBomSess ? 0 : nAdditional)`
   * i.e. a session is EITHER a spreadsheet/BOM upload (spreadsheet tile
   * shows; CAD models/drawings/additional are suppressed) OR a CAD-file
   * upload (CAD models/drawings/additional can show; spreadsheet never
   * does) — never a mix. Only "configurable" (`hasConfigurable:
   * nConfigurable > 0`) and "custom"/"standard" (unconditional) are
   * independent of this switch. This prop now gates all four, not just the
   * spreadsheet tile — see the tile block below.
   *
   * No explicit session-type field exists on `StagingSummary` yet;
   * `StagingPage` derives this from a non-empty `StagingTriage.bomHierarchy`
   * (ADR-0086 — "absent/empty when the session captured no BOM structure"),
   * the closest available proxy for `_isBomSess`. Flag to software-engineer
   * if a real `isBomSession` (or similar) field should be added to the
   * contract instead of this client-side inference.
   */
  readonly hasBomSource?: boolean
  /**
   * UID-402 (ADR-0124) — the session-wide primary-CTA decision, derived by
   * `deriveStagingCta`. `null` while there is no triage to derive from; a
   * `kind` of `'none'` (nothing staged) renders no primary CTA at all.
   */
  readonly cta: StagingCta | null
  /** True while the "Review in library" hand-off's `confirmParts` is in
   * flight, so the secondary action can show progress and not be double-fired. */
  readonly ctaBusy?: boolean
  /**
   * Gate-5 W5 — a failed "Review in library" hand-off, rendered HERE, adjacent
   * to the button that caused it. It previously shared `confirmError` with the
   * sticky selection/action card's selection-scoped confirm, so a hand-off
   * failure surfaced inside that card (`StagingActionBar`, now removed as
   * not-in-design) — a different region of the page from the control the
   * user just clicked. UID-588 — "Review in library" has no confirmation
   * dialog (product decision, 2026-09-10), so this is the ONLY surface the
   * error renders on, unconditionally, exactly like `rfqHandoffError` below.
   */
  readonly handoffError?: string | undefined
  /**
   * ADR-0130 D3 (UID-425 Bug 3) — the RFQ hand-off's OWN failure (or its
   * "nothing to confirm" explanation), rendered beside the PRIMARY CTA — a
   * different region from `handoffError` above, which sits beside "Review in
   * library". Gate-5 W5's ruling applies verbatim: these two actions must not
   * share an error region any more than they share busy/prompt state.
   */
  readonly rfqHandoffError?: string | undefined
  /**
   * UID-402 — the SECONDARY option: "add + see parts in the library". No longer
   * a bare `navigate('/parts')` (ADR-0079 §2.2) — the handler now confirms this
   * session's confirmable matched parts into the Library first and hands their
   * ids on, which is what makes the parts actually show up. See
   * `StagingPage.handleReviewInLibrary`.
   */
  readonly onReviewInLibrary: () => void
  /** Primary CTA target when the system CANNOT price everything. */
  readonly onCreateRfq: () => void
  /** Primary CTA target when the system CAN price everything (UID-402 — "go
   * straight to checkout"). */
  readonly onCheckout: () => void
}

/**
 * Upload-makeup summary strip (S-C, UID-187, `Fictiv Platform.dc.html:2533-
 * 2564`, superseded by the UID-254 amendment to ADR-0079). Renders the
 * design's own file-makeup tiles only — files uploaded, then EITHER
 * spreadsheet (BOM session) OR CAD models/drawings/additional (CAD-file
 * session), then custom/standard (and configurable when present) — plus a
 * session-wide CTA slot ("Review in library" / "Create RFQ"). This replaces
 * the previous two-row layout (a file-count row over a separate part-types
 * row). Every colored tile pairs its color with text/icon — never color
 * alone (accessibility-wcag skill).
 *
 * 2026-09-09 defect fix — the `matched`/`to resolve` tiles are REMOVED: they
 * were UID-254 build additions with no design equivalent (`Fictiv
 * Platform.dc.html:2533-2564` renders neither), and on a real session
 * (multiple file/part-type tiles plus these two) the strip grew well past
 * the design's 4-tile count, which is what was crowding out — and on
 * narrower widths pushing off-screen — the CTA group beside it. Flag to PM/
 * software-engineer if `matched`/`to resolve` counts are still wanted
 * somewhere — they should get a design pass first, not be silently
 * reintroduced here. `matchedCount` is removed from this component's props
 * along with its tile.
 *
 * 2026-09-08 defect fix — the "project filing" caption ("Not filed to a
 * project" / "Filing into project: X" / "Filed to a project") ADR-0130
 * (UID-425 Gap 2, Item 3) added next to the CTA group is REMOVED: the
 * canonical design source (`Fictiv Platform.dc.html:3037-3041`) renders
 * exactly two elements in this row — "Review in library" and the primary
 * CTA — no project-filing text anywhere near it, on this screen, or
 * anywhere else in the design. `workspaceId`/`workspaceName` are removed
 * from this component's props along with it; `StagingTriage` still carries
 * both fields (untouched server-side) for whichever other build addition
 * cited them, if any. Flag to PM/software-engineer if this information is
 * still wanted somewhere — it should get a design pass first, not be
 * silently reintroduced here.
 */
export function StagingSummaryCard({
  summary,
  hasBomSource = false,
  cta,
  ctaBusy = false,
  handoffError,
  rfqHandoffError,
  onReviewInLibrary,
  onCreateRfq,
  onCheckout,
}: StagingSummaryCardProps) {
  const files = summary?.files
  const partTypes = summary?.partTypes
  const gateCaptionId = 'staging-summary-cta-caption'
  // UID-402 — `kind: 'none'` means nothing is ACTIONABLE, so there is no next
  // step to offer; render the secondary "Review in library" only.
  const showPrimaryCta = cta !== null && cta.kind !== 'none'
  const blocked = cta?.kind === 'blocked'
  // Gate-5 W6 — rows on screen but none actionable (e.g. every matched part
  // ignored). Distinct from a genuinely empty session, which needs no
  // explanation because there is visibly nothing there.
  const nothingActionable = cta?.kind === 'none' && cta.stagedRowCount > 0

  return (
    <div className="card staging-summary-card">
      {/* ADR-0130 (UID-425 Bug 4 item 2) — the UID-255 "system is thinking"
          auto-quote-vs-manual banner that used to render here is REMOVED. The
          design (`Fictiv Platform.dc.html:9497-9498`) computes
          `stgAttnCount`/`stgReadyCount` but binds them to NO markup at all —
          this banner was never in the design. Product ruling: remove it to
          match the design. The per-row `ANALYSIS_SIGNAL_REASON_LABEL` `Chip`s
          that ADR-0124 D5 designates as the real non-colour signal are
          UNTOUCHED — they live in the row components, not here. The
          `analysisSignal` contract field and `buildStagingAnalysisSignal`
          are untouched server-side; only this now-unused prop/markup left. */}
      <div className="staging-summary-strip">
        {/* ADR-0130 (Bug 4 item 3) — labels/order updated to match the design
            (`Fictiv Platform.dc.html:2993-3044`): "files uploaded" → "files",
            "custom parts" → "custom", and a "standard" tile added
            (`partTypes.standard` was already populated server-side, just
            never rendered). `matched`/`to resolve` (former UID-254 build
            additions with no design equivalent) are REMOVED per the
            2026-09-09 defect fix above — this strip now renders design
            tiles only.

            2026-09-08 defect fix (REVISED) — see `hasBomSource`'s doc comment
            on the props interface above for the full `_isBomSess` derivation
            this reproduces (`Fictiv Platform.dc.html:10552-10554`). On a BOM/
            spreadsheet session (`hasBomSource`), CAD models/drawings/
            additional are suppressed and "spreadsheet" shows instead; on a
            CAD-file session, the reverse. "configurable" is independent of
            the switch — it shows whenever there IS a configurable count,
            either way. */}
        {files && partTypes && (
          <>
            <span className="staging-summary-tile">
              <span
                className="staging-summary-icon staging-summary-icon-neutral"
                aria-hidden="true"
              >
                <FileTextOutlined />
              </span>
              <b>{files.total}</b> file{files.total === 1 ? '' : 's'}
            </span>
            {(!hasBomSource || files.cadModels > 0) && (
              <span className="staging-summary-tile">
                <span className="staging-summary-icon staging-summary-icon-info" aria-hidden="true">
                  <AppstoreOutlined />
                </span>
                <b>{files.cadModels}</b> CAD model{files.cadModels === 1 ? '' : 's'}
              </span>
            )}
            {(!hasBomSource || files.drawings > 0) && (
              <span className="staging-summary-tile">
                <span
                  className="staging-summary-icon staging-summary-icon-warning"
                  aria-hidden="true"
                >
                  <FileTextOutlined />
                </span>
                <b>{files.drawings}</b> drawing{files.drawings === 1 ? '' : 's'}
              </span>
            )}
            {hasBomSource && (
              <span className="staging-summary-tile">
                <span
                  className="staging-summary-icon staging-summary-icon-success"
                  aria-hidden="true"
                >
                  <TableOutlined />
                </span>
                <b>1</b> spreadsheet
              </span>
            )}
            {!hasBomSource && files.additional > 0 && (
              <span className="staging-summary-tile">
                <span
                  className="staging-summary-icon staging-summary-icon-neutral"
                  aria-hidden="true"
                >
                  <FileOutlined />
                </span>
                <b>{files.additional}</b> additional
              </span>
            )}
            <span className="staging-summary-tile">
              <span
                className="staging-summary-type-icon staging-summary-type-icon-purple"
                aria-hidden="true"
              >
                <EditOutlined />
              </span>
              <b>{partTypes.custom}</b> custom
            </span>
            <span className="staging-summary-tile">
              <span
                className="staging-summary-type-icon staging-summary-type-icon-success"
                aria-hidden="true"
              >
                <CheckCircleOutlined />
              </span>
              <b>{partTypes.standard}</b> standard
            </span>
            {partTypes.configurable > 0 && (
              <span className="staging-summary-tile">
                <span
                  className="staging-summary-type-icon staging-summary-type-icon-info"
                  aria-hidden="true"
                >
                  <SettingOutlined />
                </span>
                <b>{partTypes.configurable}</b> configurable
              </span>
            )}
          </>
        )}

        {/* UID-402 — exactly ONE primary CTA plus ONE secondary option, which is
            what both the ticket's domain rules and the canonical design source
            call for (`Fictiv Platform.dc.html:3039-3040`: a "Review in library"
            outline button and a single `stgCtaStyle` primary, side by side at
            `margin-left:auto`).

            Replaces the old always-visible "Create RFQ" button + "Some items
            still need attention" caption, which was purely advisory: ADR-0079
            §4.5 explicitly kept it enabled and routing regardless, so a session
            with unresolved items sailed straight into RFQ creation. That
            advisory is now a real gate. */}
        <div className="staging-summary-cta-group">
          {/* Design fix — `Fictiv Platform.dc.html:3039` renders this button at
              38px tall next to the 40px primary CTA (`:3040`/`stgCtaStyle`),
              i.e. the two are meant to read as one visually-paired action
              group. `size="sm"` (24px) instead of `size="lg"` (40px, the
              closest step this system has to the design's 38px) made this
              button noticeably shorter than its sibling. The design also puts
              a "list" glyph before the label (the exact three-rule-and-dot
              geometry `IconListView` already hand-inlines for the parts-view
              toggle, `:1608`), colored `var(--fg-2)`/`--color-text-secondary`
              — separate from the button's own `--color-text-primary` label
              color, same as the design's own `stroke="currentColor"`-on-
              `<svg style="color:var(--fg-2)">` split. */}
          <Button variant="outline" size="lg" loading={ctaBusy} onClick={onReviewInLibrary}>
            <span style={{ color: 'var(--color-text-secondary)', display: 'inline-flex' }}>
              <IconListView size={16} />
            </span>
            Review in library
          </Button>
          {showPrimaryCta && cta && (
            <div className="staging-summary-rfq-group">
              {/* Gate-5 W8 — `aria-disabled` + still focusable + a no-op
                  handler, NOT the native `disabled` attribute. A `disabled`
                  button is removed from the tab order, so a keyboard user could
                  never reach it and the `aria-describedby` association that
                  explains WHY it is unavailable never fired for them. This is
                  the standard accessible-gating pattern: the control stays
                  reachable and announces its own reason. */}
              <Button
                variant="primary"
                size="lg"
                className={cx(
                  'staging-summary-cta',
                  cta.kind !== 'checkout' && 'staging-summary-cta-rfq',
                )}
                aria-disabled={blocked || undefined}
                onClick={blocked ? undefined : cta.kind === 'checkout' ? onCheckout : onCreateRfq}
                aria-describedby={blocked ? gateCaptionId : undefined}
              >
                {cta.kind === 'checkout' ? (
                  <IconCartGlyph size={15} />
                ) : (
                  <IconPaperPlane size={15} />
                )}
                {STAGING_CTA_LABEL[cta.kind]}
              </Button>
              {/* Gate-5 W8 — a polite live region, so resolving the LAST
                  blocking item (which flips this caption away and enables the
                  CTA) is announced rather than silently changing under a screen
                  reader. `<output>` carries an implicit `role="status"`, the same
                  primitive the analysis banner above uses. */}
              <output id={gateCaptionId} className="caption-text staging-cta-caption">
                {blocked
                  ? `${cta.needsResolutionCount} item${
                      cta.needsResolutionCount === 1 ? ' still needs' : 's still need'
                    } resolution`
                  : ''}
              </output>
              {/* ADR-0130 D3 — the RFQ hand-off's own failure/explanation,
                  beside the button that caused it. "Create RFQ" has no
                  confirmation dialog (product decision, 2026-09-03), so this
                  is the only surface the error renders on — unlike
                  `handoffError` above, there is nothing to suppress it while
                  open. */}
              {rfqHandoffError && (
                <p role="alert" className="staging-row-inline-error">
                  {rfqHandoffError}
                </p>
              )}
            </div>
          )}
          {/* Gate-5 W6 — say WHY there is no next step, rather than rendering an
              unexplained gap next to rows the user can still see. Reached when
              every staged row is set aside (e.g. all matches ignored): there is
              genuinely nothing to price, quote or order, so offering a CTA would
              be a dead end — but silence read as a bug. */}
          {nothingActionable && (
            <p className="caption-text staging-cta-caption">
              Nothing here can be quoted or ordered yet — every part has been ignored or set aside.
              Un-ignore a match, or review these parts in your library.
            </p>
          )}
          {/* Gate-5 W5 — the hand-off's own failure, beside the hand-off button
              that caused it (it used to surface inside the sticky
              selection/action card, `StagingActionBar` — now removed as
              not-in-design — a different region of the page entirely).
              UID-588 — "Review in library" has no confirmation dialog
              (product decision, 2026-09-10), so this is the only surface the
              error renders on — unconditionally, unlike the sibling
              `rfqHandoffError` block above, which was always unconditional. */}
          {handoffError && (
            <p role="alert" className="staging-row-inline-error">
              {handoffError}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
